// utils/impresionBluetooth.js

/**
 * Función utilitaria para eliminar tildes y caracteres especiales.
 * Las impresoras térmicas chinas/genéricas suelen fallar con UTF-8 si no se configura su página de códigos.
 */
const removerTildes = (texto) => {
    if (!texto) return '';
    return texto.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
};

/**
 * Rellena con espacios para alinear a la izquierda y derecha en la misma línea
 */
const textoIzquierdaDerecha = (izq, der, ancho) => {
    const textIzq = izq.toString();
    const textDer = der.toString();
    const espacios = ancho - textIzq.length - textDer.length;
    
    if (espacios <= 0) return textIzq.substring(0, ancho - textDer.length - 1) + " " + textDer;
    return textIzq + " ".repeat(espacios) + textDer;
};

/**
 * Centra un texto rellenando con espacios a los lados
 */
const textoCentrado = (texto, ancho) => {
    const txt = texto.toString();
    if (txt.length >= ancho) return txt.substring(0, ancho);
    const espacios = Math.floor((ancho - txt.length) / 2);
    return " ".repeat(espacios) + txt;
};

/**
 * Genera el payload de bytes (ESC/POS) para enviar a la impresora
 */
export const generarTicketBluetooth = (datosBase, infoNegocio, configAncho = '80mm') => {
    // Definir el ancho en caracteres (80mm = ~48 caracteres, 57mm = ~32 caracteres)
    const ANCHO = configAncho === '80mm' ? 48 : 32;

    // Comandos ESC/POS estándar
    const CMD = {
        INIT: [27, 64],                   // ESC @ (Inicializar)
        ALIGN_LEFT: [27, 97, 0],          // ESC a 0
        ALIGN_CENTER: [27, 97, 1],        // ESC a 1
        ALIGN_RIGHT: [27, 97, 2],         // ESC a 2
        BOLD_ON: [27, 69, 1],             // ESC E 1
        BOLD_OFF: [27, 69, 0],            // ESC E 0
        TEXT_NORMAL: [29, 33, 0],         // GS ! 0
        TEXT_DOUBLE: [29, 33, 17],        // GS ! 17 (Doble alto y ancho)
        CUT_PAPER: [29, 86, 65, 0],       // GS V A 0 (Corte total)
    };

    let buffer = [];
    const encoder = new TextEncoder();

    // Helper para inyectar comandos
    const addCmd = (comando) => buffer.push(new Uint8Array(comando));
    // Helper para inyectar texto
    const addText = (texto) => buffer.push(encoder.encode(removerTildes(texto) + '\n'));
    
    const lineaSeparadora = "-".repeat(ANCHO);
    const lineaPunteada = ".".repeat(ANCHO);

    // --- INICIO DEL TICKET ---
    addCmd(CMD.INIT);
    
    // 1. CABECERA (Info del Negocio)
    addCmd(CMD.ALIGN_CENTER);
    addCmd(CMD.BOLD_ON);
    addCmd(CMD.TEXT_DOUBLE);
    addText(infoNegocio?.nombre || 'NEGOCIO');
    
    addCmd(CMD.TEXT_NORMAL);
    addCmd(CMD.BOLD_OFF);
    if (infoNegocio?.nombre_legal && infoNegocio.nombre_legal !== infoNegocio.nombre) {
        addText(infoNegocio.nombre_legal);
    }
    addText(`NIT: ${infoNegocio?.nit || '900.000.000-1'}`);
    addText(infoNegocio?.direccion || 'Direccion no registrada');
    addText(`TEL: ${infoNegocio?.telefono || '0000000000'}`);
    addText(lineaSeparadora);

    // 2. INFO DEL RECIBO Y CLIENTE
    addCmd(CMD.ALIGN_LEFT);
    addCmd(CMD.BOLD_ON);
    
    const numeroMostrar = datosBase.numeroFactura || 
                         (datosBase.ventaId.includes('-') ? datosBase.ventaId.split('-')[0].toUpperCase() : datosBase.ventaId);
    
    const tituloDoc = datosBase.tituloDocumento || 'RECIBO';
    addText(`${tituloDoc}: ${numeroMostrar}`);
    addCmd(CMD.BOLD_OFF);
    
    let fechaTexto = datosBase.fechaVenta;
    if (!fechaTexto || isNaN(new Date(fechaTexto).getTime())) {
        fechaTexto = new Date();
    }
    const fecha = new Date(fechaTexto);
    const fechaFormateada = `${fecha.toLocaleDateString()} ${fecha.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}`;
    addText(`FECHA: ${fechaFormateada}`);
    addText(`CAJERO: ${datosBase.cajero}`);
    addText(`MEDIO PAGO: ${datosBase.metodoPago || 'EFECTIVO'}`);
    addText(lineaSeparadora);
    
    addCmd(CMD.BOLD_ON);
    addText(`CLIENTE: ${datosBase.clienteNombre || 'CONSUMIDOR FINAL'}`);
    addCmd(CMD.BOLD_OFF);
    addText(`CC/NIT: ${datosBase.clienteDocumento || '222222222222'}`);
    if (datosBase.clienteTelefono) addText(`TEL: ${datosBase.clienteTelefono}`);
    if (datosBase.clienteDireccion) addText(`DIR: ${datosBase.clienteDireccion}`);
    addText(lineaSeparadora);

    // 3. TABLA DE ARTÍCULOS
    // Cabecera de la tabla
    addCmd(CMD.BOLD_ON);
    if (ANCHO === 48) {
        addText("CANT  DESCRIPCION             V.UNIT     TOTAL");
    } else {
        addText("CANT DESCRIPCION      TOTAL");
    }
    addCmd(CMD.BOLD_OFF);
    
    datosBase.carrito.forEach(item => {
        const cantidad = item.numItems || item.cantidad || 1;
        const precioVenta = item.precioVenta || 0;
        const descuento = Number(item.descuento || 0);
        const precioFinalItem = precioVenta * (1 - descuento / 100);
        const totalItem = precioFinalItem * cantidad;

        // 1. Imprimimos el nombre completo en su propia línea (sin chocar con nada)
        addText(item.nombre.toUpperCase());

        // 2. En la línea de abajo ponemos la cantidad, valor unitario y total perfectamente alineados
        if (ANCHO === 48) {
            const parteIzq = `${cantidad} un x $${Number(precioFinalItem).toLocaleString('es-CO')}`;
            const parteDer = `$${Number(totalItem).toLocaleString('es-CO')}`;
            addText(textoIzquierdaDerecha(parteIzq, parteDer, ANCHO));
        } else {
            let descLine = `${cantidad} x $${Number(precioFinalItem).toLocaleString('es-CO')}`;
            let totLine = `$${Number(totalItem).toLocaleString('es-CO')}`;
            addText(textoIzquierdaDerecha(descLine, totLine, ANCHO));
        }
        addText(lineaPunteada);
    });

    // 4. TOTALES
    const num = (val) => `$${Number(val || 0).toLocaleString('es-CO')}`;
    const t = datosBase.totales;

    addText(textoIzquierdaDerecha("SUBTOTAL:", num(t.subtotal || t.total), ANCHO));
    if (t.impuestos > 0) addText(textoIzquierdaDerecha("IVA/IMPUESTOS:", num(t.impuestos), ANCHO));
    if (t.totalDescuento > 0) addText(textoIzquierdaDerecha("DESCUENTO:", "-" + num(t.totalDescuento), ANCHO));
    
    addText(lineaSeparadora);
    
    addCmd(CMD.BOLD_ON);
    addCmd(CMD.TEXT_DOUBLE);
    const textoTotal = datosBase.metodoPago === 'Credito' ? 'TOTAL FACTURA' : 'TOTAL PAGO';
    addText(textoIzquierdaDerecha(textoTotal, num(t.total), Math.floor(ANCHO / 2))); // Dividimos ancho por el TEXT_DOUBLE
    addCmd(CMD.TEXT_NORMAL);
    addCmd(CMD.BOLD_OFF);

    // 5. DETALLES DE PAGO
    addText(lineaSeparadora);
    if (datosBase.metodoPago === 'Efectivo') {
        addText(textoIzquierdaDerecha("EFECTIVO RECIBIDO:", num(datosBase.efectivoRecibido || t.total), ANCHO));
        addText(textoIzquierdaDerecha("CAMBIO ENTREGADO:", num(datosBase.cambioEntregado || 0), ANCHO));
    } else if (datosBase.metodoPago === 'Mixto') {
        addText(textoIzquierdaDerecha("EFECTIVO:", num(datosBase.efectivoRecibido), ANCHO));
        addText(textoIzquierdaDerecha("TRANSFERENCIA:", num(datosBase.montoTransferencia), ANCHO));
    } else if (datosBase.metodoPago === 'Credito' && Number(datosBase.efectivoRecibido) > 0) {
        addText(textoIzquierdaDerecha("ABONO INICIAL:", num(datosBase.efectivoRecibido), ANCHO));
        addCmd(CMD.BOLD_ON);
        addText(textoIzquierdaDerecha("SALDO PENDIENTE:", num(t.total - datosBase.efectivoRecibido), ANCHO));
        addCmd(CMD.BOLD_OFF);
    } else {
        addText(textoIzquierdaDerecha(`PAGADO EN ${datosBase.metodoPago.toUpperCase()}:`, num(t.total), ANCHO));
    }

    // 6. TEXTOS LEGALES
    addText(lineaSeparadora);
    addCmd(CMD.ALIGN_CENTER);
    addCmd(CMD.BOLD_ON);
    addText("*** DOCUMENTO EQUIVALENTE POS ***");
    addCmd(CMD.BOLD_OFF);
    addText("ESTE DOCUMENTO NO CONSTITUYE UNA");
    addText("FACTURA ELECTRONICA DE VENTA.");
    addText("No se aceptan reclamos ni devoluciones");
    addText("despues de 3 dias calendario.");
    
    addText(lineaPunteada);
    // Para textos largos, el salto de línea natural de la impresora lo corta a veces feo. 
    // Es mejor mandarlo continuo y que la impresora lo baje.
    addText(`Estimado cliente, segun la ley estatutaria 1581 de 2012 habeas data, ${infoNegocio?.nombre_legal || 'la empresa'} manejara los datos suministrados para fines informativos y promocionales.`);
    
    // 7. FOOTER
    addText(lineaSeparadora);
    addCmd(CMD.BOLD_ON);
    addText("SISTEMA POS GENERADO POR YUGONUB S.A.S");
    addText("SITIO WEB: WWW.POS.YUGONUB.COM");
    addText("TELEFONO: 3147787028");
    addCmd(CMD.BOLD_OFF);

    // Espacio final y corte
    addText("\n\n\n\n");
    addCmd(CMD.CUT_PAPER);

    // --- CONCATENAR TODO EN UN SOLO UINT8ARRAY ---
    const longitudTotal = buffer.reduce((acc, val) => acc + val.length, 0);
    const payload = new Uint8Array(longitudTotal);
    let offset = 0;
    for (let array of buffer) {
        payload.set(array, offset);
        offset += array.length;
    }

    return payload;
};